import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as ace from 'ace-builds';

@Component({
  selector: 'app-vinter-code-editor',
  templateUrl: './vinter-code-editor.component.html',
  styleUrls: ['./vinter-code-editor.component.scss']
})
export class VinterCodeEditor implements AfterViewInit, OnChanges {
  @ViewChild('editor', { static: true }) private editorEl!: ElementRef<HTMLElement>;

  @Input() fontSize: number = 15;
  @Input() width!: number;
  @Input() height: number = 450;
  @Input() disabled: boolean = false;
  @Input() codeType: string = '';
  @Input() template: string = '';
  @Input() isSelectionEnabled: boolean = false;
  @Input() dropdownReadonly: boolean = false;
  @Output() onCodeInputChanged = new EventEmitter<{ codeText: string; codeLanguage: any }>();

  codeModes = [
    { name: '-', code: '-' },
    { name: 'Python', code: 'python' },
    { name: 'Javascript', code: 'javascript' },
    { name: 'CSharp', code: 'csharp' },
    { name: 'Java', code: 'java' },
    { name: 'Php', code: 'php' }
  ];

  codeLanguagesModes = [
    { name: 'Python', code: '84377a79-8bf8-431a-9a72-eaee4e860566' },
    { name: 'Javascript', code: '7c34ac08-05d1-4174-a76b-5ee3e201a247' },
    { name: 'CSharp', code: '1232fec2-83f2-4dda-8e5c-e32e379c7c92' },
    { name: 'Java', code: '49bdab69-a16a-4f09-82f2-0e2707555c9b' },
    { name: 'Php', code: '23b2ccef-c0a2-42ca-86f2-1b556808ad39' }
  ];
  selectedCodeMode = this.codeModes.find(x => x.code === this.codeType) ?? this.codeModes[0];
  private editorInstance!: ace.Ace.Editor;

  ngAfterViewInit(): void {
    this.updateSelectedCodeMode();

    ace.config.set('basePath', '/node_modules/ace-builds/src-min-noconflict');
    ace.require('ace/ext/language_tools');

    this.editorInstance = ace.edit(this.editorEl.nativeElement);
    this.editorInstance.setOptions({
      enableBasicAutocompletion: true,
      enableSnippets: true,
      enableLiveAutocompletion: false,
      cursorStyle: 'smooth'
    });
    this.editorInstance.setTheme('ace/theme/twilight');

    this.editorInstance.getSession().on('change', () => {
      this.onCodeInputChanged.emit({
        codeText: this.editorInstance.getValue(),
        codeLanguage: this.selectedCodeMode
      });
    });

    this.applyAllSettings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('codeType' in changes) {
      this.updateSelectedCodeMode();
    }
    if (this.editorInstance) {
      this.applyAllSettings();
    }
  }

  private updateSelectedCodeMode(): void {
    const langName = this.codeLanguagesModes.find(x => x.code === this.codeType)?.name;
    const modeCode = langName?.toLowerCase() ?? this.codeType;
    const found = this.codeModes.find(x => x.code === modeCode);
    if (found) this.selectedCodeMode = found;
  }

  private applyAllSettings(): void {
    this.editorInstance.getSession().setMode(`ace/mode/${this.selectedCodeMode.code}`);
    this.editorInstance.getSession().setValue(this.template);
    this.editorInstance.setReadOnly(this.disabled || this.dropdownReadonly);
    this.editorInstance.setFontSize(`${this.fontSize}px`);
  }

  onCodeLanguageChanged(event: any): void {
    const mode = this.codeModes.find(x => x.name === event.code);
    if (!mode) return;
    this.codeType = this.codeLanguagesModes.find(x => x.name === event.code)?.code ?? mode.code;
    this.selectedCodeMode = mode;
    this.editorInstance.getSession().setMode(`ace/mode/${mode.code}`);
    this.onCodeInputChanged.emit({
      codeText: this.editorInstance.getValue(),
      codeLanguage: this.selectedCodeMode
    });
  }

  reset(): void {
    this.editorInstance.getSession().setValue(this.template);
  }
}
